import * as yup from 'yup'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useForm, FormProvider, useWatch } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import { Col, Row, Upload } from 'antd'

import HookedField from 'components/HookedField'
import Button from 'components/Button'
import Icon from 'components/Icon'
import { ICONS } from 'utils/constants'
import { getProducts, getProductSizes } from 'redux/actions/products'
import { getCategories } from 'redux/actions/categories'

const SUBSCRIPTION_CATEGORY_TYPE = 'SubscriptionType'

const advertisementTypeOptions = [
  { label: 'Header', value: 'HEADER' },
  { label: 'Footer', value: 'FOOTER' },
]

const LINK_TYPE_NONE = 'NONE'
const linkTypeOptions = [
  { label: 'No Link', value: LINK_TYPE_NONE },
  { label: 'Category', value: 'CATEGORY' },
  { label: 'Product', value: 'PRODUCT' },
]

const advertisementSchema = yup.object().shape({
  advertisementType: yup.string().required('This field is required'),
  linkType: yup
    .string()
    .transform((value) => (value === '' || value == null ? LINK_TYPE_NONE : value))
    .oneOf([LINK_TYPE_NONE, 'CATEGORY', 'PRODUCT'])
    .required(),
  productId: yup
    .number()
    .nullable()
    .transform((value, originalValue) => {
      if (originalValue === '' || originalValue == null) return null
      const n = Number(originalValue)
      return Number.isNaN(n) ? null : n
    })
    .when('linkType', {
      is: 'PRODUCT',
      then: (schema) => schema.required('This field is required'),
      otherwise: (schema) => schema.nullable().notRequired(),
    }),
  categoryId: yup
    .number()
    .nullable()
    .transform((value, originalValue) => {
      if (originalValue === '' || originalValue == null) return null
      const n = Number(originalValue)
      return Number.isNaN(n) ? null : n
    })
    .when('linkType', {
      is: 'CATEGORY',
      then: (schema) => schema.required('This field is required'),
      otherwise: (schema) => schema.nullable().notRequired(),
    }),
  productSizes: yup
    .array()
    .of(yup.string())
    .nullable()
    .transform((value) => (Array.isArray(value) ? value : value ? [value] : [])),
  isVisible: yup.boolean(),
})

const AdvertisementForm = ({ advertisement, submitHandler, loading }) => {
  const { t } = useTranslation()

  const defaultValues = useMemo(
    () => ({
      advertisementType: advertisement?.advertisementType || 'HEADER',
      linkType:
        advertisement?.linkType ||
        (advertisement?.categoryId
          ? 'CATEGORY'
          : advertisement?.productId
          ? 'PRODUCT'
          : LINK_TYPE_NONE),
      productId: advertisement?.productId || null,
      categoryId: advertisement?.categoryId || null,
      productSizes: advertisement?.productSizes || [],
      isVisible: advertisement?.isVisible ?? true,
    }),
    [advertisement],
  )

  const [fileList, setFileList] = useState(() => {
    if (advertisement?.image?.src && advertisement?.image?.id) {
      return [
        {
          uid: advertisement.image.id,
          name: advertisement.image.filename || 'image',
          status: 'done',
          url: advertisement.image.src,
        },
      ]
    }
    return []
  })

  const [bannerFileList, setBannerFileList] = useState(() => {
    if (advertisement?.bannerImage?.src && advertisement?.bannerImage?.id) {
      return [
        {
          uid: advertisement.bannerImage.id,
          name: advertisement.bannerImage.filename || 'banner-image',
          status: 'done',
          url: advertisement.bannerImage.src,
        },
      ]
    }
    return []
  })

  const [fileListAr, setFileListAr] = useState(() => {
    if (advertisement?.imageAr?.src && advertisement?.imageAr?.id) {
      return [
        {
          uid: advertisement.imageAr.id,
          name: advertisement.imageAr.filename || 'image-ar',
          status: 'done',
          url: advertisement.imageAr.src,
        },
      ]
    }
    return []
  })

  const [bannerFileListAr, setBannerFileListAr] = useState(() => {
    if (advertisement?.bannerImageAr?.src && advertisement?.bannerImageAr?.id) {
      return [
        {
          uid: advertisement.bannerImageAr.id,
          name: advertisement.bannerImageAr.filename || 'banner-image-ar',
          status: 'done',
          url: advertisement.bannerImageAr.src,
        },
      ]
    }
    return []
  })

  const methods = useForm({
    mode: 'onSubmit',
    shouldFocusError: true,
    defaultValues,
    resolver: yupResolver(advertisementSchema),
  })

  const linkType = useWatch({ control: methods.control, name: 'linkType' })
  const selectedCategoryId = useWatch({ control: methods.control, name: 'categoryId' })

  useEffect(() => {
    if (linkType === undefined || linkType === null || linkType === '') {
      methods.setValue('linkType', LINK_TYPE_NONE, { shouldDirty: false })
    }
  }, [linkType, methods])

  const dispatch = useDispatch()
  const products = useSelector((state) => state.products.items) || []
  const productSizeOptions = useSelector((state) => state.products.productSizes) || []
  const categories = useSelector((state) => state.categories.items) || []

  const selectedCategory = useMemo(
    () => (selectedCategoryId ? categories.find((c) => c.id === selectedCategoryId) : null),
    [categories, selectedCategoryId],
  )
  const isSubscriptionCategory = selectedCategory?.categoryType === SUBSCRIPTION_CATEGORY_TYPE

  const previousCategoryIdRef = useRef(selectedCategoryId)

  useEffect(() => {
    // Fetch a wide list so the selects have everything available.
    dispatch(getProducts({ page: 1, size: 500, isSubscriptionVisible: false, isVisible: true }))
    dispatch(getCategories({ page: 1, size: 500, isSubscriptionVisible: true }))
  }, [dispatch])

  useEffect(() => {
    if (linkType !== 'CATEGORY') return
    dispatch(getProductSizes(selectedCategoryId && !isSubscriptionCategory ? { categoryId: selectedCategoryId } : undefined))

  }, [dispatch, linkType, selectedCategoryId, isSubscriptionCategory])

  useEffect(() => {
    const previous = previousCategoryIdRef.current
    if (previous !== selectedCategoryId) {
      previousCategoryIdRef.current = selectedCategoryId
      // Skip the very first time this effect runs (initial mount / form reset).
      if (previous !== undefined) {
        methods.setValue('productSizes', [], { shouldDirty: true })
      }
    }
  }, [selectedCategoryId, methods])

  useEffect(() => {
    const current = methods.getValues('productSizes') || []
    if (!current.length) return
    const valid = current.filter((s) => productSizeOptions.includes(s))
    if (valid.length !== current.length) {
      methods.setValue('productSizes', valid, { shouldDirty: true })
    }
  }, [productSizeOptions, methods])

  const handleUploadChange = ({ fileList: newFileList }) => {
    // keep only one image
    const latestList = newFileList.slice(-1)
    setFileList(latestList)
  }

  const handleBannerUploadChange = ({ fileList: newFileList }) => {
    const latestList = newFileList.slice(-1)
    setBannerFileList(latestList)
  }

  const handleUploadChangeAr = ({ fileList: newFileList }) => {
    const latestList = newFileList.slice(-1)
    setFileListAr(latestList)
  }

  const handleBannerUploadChangeAr = ({ fileList: newFileList }) => {
    const latestList = newFileList.slice(-1)
    setBannerFileListAr(latestList)
  }

  useEffect(() => {
    methods.reset(defaultValues)
    // Reset fileList when advertisement changes
    if (advertisement?.image?.src && advertisement?.image?.id) {
      setFileList([
        {
          uid: advertisement.image.id,
          name: advertisement.image.filename || 'image',
          status: 'done',
          url: advertisement.image.src,
        },
      ])
    } else {
      setFileList([])
    }

    if (advertisement?.bannerImage?.src && advertisement?.bannerImage?.id) {
      setBannerFileList([
        {
          uid: advertisement.bannerImage.id,
          name: advertisement.bannerImage.filename || 'banner-image',
          status: 'done',
          url: advertisement.bannerImage.src,
        },
      ])
    } else {
      setBannerFileList([])
    }

    if (advertisement?.imageAr?.src && advertisement?.imageAr?.id) {
      setFileListAr([
        {
          uid: advertisement.imageAr.id,
          name: advertisement.imageAr.filename || 'image-ar',
          status: 'done',
          url: advertisement.imageAr.src,
        },
      ])
    } else {
      setFileListAr([])
    }

    if (advertisement?.bannerImageAr?.src && advertisement?.bannerImageAr?.id) {
      setBannerFileListAr([
        {
          uid: advertisement.bannerImageAr.id,
          name: advertisement.bannerImageAr.filename || 'banner-image-ar',
          status: 'done',
          url: advertisement.bannerImageAr.src,
        },
      ])
    } else {
      setBannerFileListAr([])
    }
  }, [defaultValues, methods, advertisement])

  const onSubmit = useCallback(
    (formData) => {
      const imageFile = fileList.find((f) => f.originFileObj)?.originFileObj || null
      const bannerImageFile =
        bannerFileList.find((f) => f.originFileObj)?.originFileObj || null
      const imageArFile = fileListAr.find((f) => f.originFileObj)?.originFileObj || null
      const bannerImageArFile =
        bannerFileListAr.find((f) => f.originFileObj)?.originFileObj || null
      const isCategory = formData.linkType === 'CATEGORY'
      const isProduct = formData.linkType === 'PRODUCT'
      const linkType = isCategory || isProduct ? formData.linkType : null
      const prepared = {
        advertisementType: formData.advertisementType,
        linkType,
        productId: isProduct ? formData.productId : null,
        categoryId: isCategory ? formData.categoryId : null,
        productSizes: isCategory ? formData.productSizes || [] : null,
        isVisible: formData.isVisible ?? true,
        imageFile,
        bannerImageFile,
        imageArFile,
        bannerImageArFile,
      }

      submitHandler && submitHandler(prepared)
    },
    [submitHandler, fileList, bannerFileList, fileListAr, bannerFileListAr],
  )

  return (
    <FormProvider {...methods}>
      <form className="form" autoComplete="off" onSubmit={methods.handleSubmit(onSubmit)}>
        <Row gutter={[15, 10]}>
          {/* Images (English + dark mode) */}
          <Col span={24} md={{ span: 12 }}>
            <div className="field">
              <div className="field-header">
                <label>{t('advertisements.banner image', 'Banner image (English)')}</label>
              </div>
              <div className="field-wrapper">
                <Upload
                  name="file"
                  listType="picture-card"
                  beforeUpload={() => false}
                  fileList={fileList}
                  maxCount={1}
                  multiple={false}
                  onChange={handleUploadChange}
                  showUploadList={{ showPreviewIcon: false }}
                >
                  {fileList.length >= 1 ? null : (
                    <div>
                      <div style={{ marginTop: 8 }}>{t('advertisements.upload image', 'Upload image')}</div>
                    </div>
                  )}
                </Upload>
              </div>
            </div>
          </Col>

          <Col span={24} md={{ span: 12 }}>
            <div className="field">
              <div className="field-header">
                <label>{t('advertisements.banner image dark', 'Banner image — dark mode (English)')}</label>
              </div>
              <div className="field-wrapper">
                <Upload
                  name="file"
                  listType="picture-card"
                  beforeUpload={() => false}
                  fileList={bannerFileList}
                  maxCount={1}
                  multiple={false}
                  onChange={handleBannerUploadChange}
                  showUploadList={{ showPreviewIcon: false }}
                >
                  {bannerFileList.length >= 1 ? null : (
                    <div>
                      <div style={{ marginTop: 8 }}>{t('advertisements.upload image', 'Upload image')}</div>
                    </div>
                  )}
                </Upload>
              </div>
            </div>
          </Col>

          {/* Arabic locale banners */}
          <Col span={24} md={{ span: 12 }}>
            <div className="field">
              <div className="field-header">
                <label>{t('advertisements.banner image ar', 'Banner image (Arabic)')}</label>
              </div>
              <div className="field-wrapper">
                <Upload
                  name="file"
                  listType="picture-card"
                  beforeUpload={() => false}
                  fileList={fileListAr}
                  maxCount={1}
                  multiple={false}
                  onChange={handleUploadChangeAr}
                  showUploadList={{ showPreviewIcon: false }}
                >
                  {fileListAr.length >= 1 ? null : (
                    <div>
                      <div style={{ marginTop: 8 }}>{t('advertisements.upload image', 'Upload image')}</div>
                    </div>
                  )}
                </Upload>
              </div>
            </div>
          </Col>

          <Col span={24} md={{ span: 12 }}>
            <div className="field">
              <div className="field-header">
                <label>
                  {t('advertisements.banner image dark ar', 'Banner image — dark mode (Arabic)')}
                </label>
              </div>
              <div className="field-wrapper">
                <Upload
                  name="file"
                  listType="picture-card"
                  beforeUpload={() => false}
                  fileList={bannerFileListAr}
                  maxCount={1}
                  multiple={false}
                  onChange={handleBannerUploadChangeAr}
                  showUploadList={{ showPreviewIcon: false }}
                >
                  {bannerFileListAr.length >= 1 ? null : (
                    <div>
                      <div style={{ marginTop: 8 }}>{t('advertisements.upload image', 'Upload image')}</div>
                    </div>
                  )}
                </Upload>
              </div>
            </div>
          </Col>

          {/* Title & Description are intentionally hidden per new Ads UX */}
          <Col span={24} md={{ span: 12 }}>
            <HookedField
              label={'Placement'}
              name="advertisementType"
              placeholder={'Placement'}
              fieldType="selectField"
              type="select"
              options={advertisementTypeOptions}
              allowClear={false}
              isRequired={true}
            />
          </Col>

          <Col span={24} md={{ span: 12 }}>
            <HookedField
              label={'Link Target'}
              name="linkType"
              placeholder={'No Link'}
              fieldType="selectField"
              type="select"
              options={linkTypeOptions}
              allowClear={true}
              isRequired={false}
            />
          </Col>

          {linkType === 'PRODUCT' && (
            <Col span={24} md={{ span: 12 }}>
              <HookedField
                label={'Select Product'}
                name="productId"
                placeholder={'Select Product'}
                fieldType="selectField"
                type="select"
                allowClear={true}
                showSearch={true}
                optionFilterProp="label"
                options={(products || []).map((p) => ({
                  label: `${p.title} - ${p.sapId ?? ''}`,
                  value: p.id,
                }))}
                isRequired={true}
              />
            </Col>
          )}

          {linkType === 'CATEGORY' && (
            <>
              <Col span={24} md={{ span: 12 }}>
                <HookedField
                  label={'Select Category'}
                  name="categoryId"
                  placeholder={'Select Category'}
                  fieldType="selectField"
                  type="select"
                  allowClear={true}
                  showSearch={true}
                  optionFilterProp="label"
                  options={(categories || []).map((c) => ({
                    label: c.name,
                    value: c.id,
                  }))}
                  isRequired={true}
                />
              </Col>

              <Col span={24} md={{ span: 12 }}>
                <HookedField
                  label={'Product Sizes (optional)'}
                  name="productSizes"
                  placeholder={'Filter by product sizes'}
                  fieldType="selectField"
                  type="select"
                  mode="multiple"
                  allowClear={true}
                  showSearch={true}
                  optionFilterProp="label"
                  options={(productSizeOptions || []).map((s) => ({
                    label: s,
                    value: s,
                  }))}
                />
              </Col>
            </>
          )}

          <Col span={24}>
            <div className="form-visibility">
              <Icon className="form-visibility-icon" icon={ICONS.EYE} size={24} />
              <span className="form-visibility-text">{t('products.switch') || t('fields.visibility') || 'Visibility'}</span>
              <HookedField
                className="form-visibility-switch"
                name="isVisible"
                fieldType="customField"
                type="switch"
              />
            </div>
          </Col>
          <Col span={24} style={{ textAlign: 'center' }}>
            <Button
              className="save-btn"
              type="primary"
              size="large"
              htmlType="submit"
              disabled={loading}
              loading={loading}
            >
              {t('app.save')}
            </Button>
          </Col>
        </Row>
      </form>
    </FormProvider>
  )
}

export default AdvertisementForm
